import React, { useMemo } from 'react'
import { addMonths } from 'date-fns';
import { scaleTime, scaleLinear } from '@visx/scale'
import { useTooltip } from "@visx/tooltip"
import { withParentSize } from '@visx/responsive'
import { max, min } from 'd3-array'
import AreaChart from './LineAreaChart'
import GraphTooltipBubble from './GraphTooltipBubble'
import GraphTooltipArea from './GraphTooltipArea'

import { getValue, getLayersExtents, getDate } from './graphUtils'
import { Camera } from '@mui/icons-material';

function GraphChart({
  parentWidth,
  height: parentHeight,
  margin = { top: 0, bottom: 0, left: 0, right: 0 },
  layers,
  domain,
}) {
  const tooltip = useTooltip()
  const axisHeight = 35
  const width = parentWidth - margin.left - margin.right
  const graphHeight = parentHeight - axisHeight
  const currentDate = new Date();  
  // bounds
  const xMax = Math.max(width, 0)
  const yMax = Math.max(graphHeight, 0)

  const getDate = (d) => {
    const date = new Date(d.date); // Assuming d.date holds your date
    return date;
  };

  const xScale = useMemo(() => {
    const extents = getLayersExtents(layers, getDate);
    const futureDate = addMonths(extents[1], 3); // Extend the domain by 3 months
  
    return scaleTime({
      range: [0, xMax],
      domain: [extents[0], futureDate], // Use futureDate to ensure the x-axis extends into the future
    });
  }, [xMax, layers]);
  

  // Old code that show the graph till today
  // const xScale = useMemo(() => {
  //   if (domain.includes(null)) {
  //     const extents = getLayersExtents(layers, getDate)
  //     return scaleTime({
  //       range: [0, xMax],
  //       domain: [extents[0], domain[1] || extents[1]]
  //     })
  //   } else {
  //     return scaleTime({
  //       range: [0, xMax],
  //       domain,
  //     })
  //   }
  // }, [xMax, domain, layers])

  
  const yScales = useMemo(() =>
    layers.map((layer) => scaleLinear({
        range: [yMax, 0],
        domain: layer.originalYDomain || [min(layer.data, getValue), max(layer.data, getValue) || 0],
        nice: true,
      })),
    [yMax, layers],
  )

  const chartAxis = useMemo(() => (
      xScale.length && yScales[0] &&
        <AreaChart
          left={margin.left}
          data={layers[0].data}
          name={'xaxis'}
          width={width}
          yMax={yMax}
          xScale={xScale}
          yScale={yScales[0]}
          key={'chartXAxis'}
          hideLeftAxis
          area={false}
          line={false}
          hideBottomAxis={false}
        />
      )
  ,[layers, xScale, yScales, margin, width, yMax])

  const chartsAreas = useMemo(() => (
    layers.map((layer, i) => (
      xScale.length && yScales[i] && layer.display === 'area' &&
        <AreaChart
          left={margin.left}
          data={layer.data}
          name={layer.name}
          width={width}
          yMax={yMax}
          xScale={xScale}
          yScale={yScales[i]}
          color={layer.color}
          key={`${layer.name}-${i}-areachart`}
          hideLeftAxis
          line={false}
          hideBottomAxis={i !== 0}
        />
      ))
    )
  ,[layers, xScale, yScales, margin, width, yMax])

  // const chartsLines = useMemo(() => (
  //   layers.map((layer, i) => (
  //     xScale.length && yScales[i] && ['line', 'area'].includes(layer.display) &&
  //       <AreaChart
  //         left={margin.left}
  //         data={layer.data}
  //         name={layer.name}
  //         width={width}
  //         yMax={yMax}
  //         xScale={xScale}
  //         yScale={yScales[i]}
  //         color={layer.color}
  //         key={`${layer.name}-${i}-linechart`}
  //         hideLeftAxis
  //         area={false}
  //         hideBottomAxis={true}
  //       />
  //     ))
  //   )
  // ,[layers, xScale, yScales, margin, width, yMax])

  const chartsLines = useMemo(() => (
    layers.map((layer, i) => {
      // const pastData = layer.data.filter((d) => getDate(d) <= currentDate); // Data points in the past or present
      // const futureData = layer.data.filter((d) => getDate(d) > currentDate); // Data points in the future

      // if (pastData.length > 0 && futureData.length > 0) {
      //   futureData.unshift(pastData[pastData.length - 1]);
      // }      

      // if (layer?.type && layer.type !== "nowcasting" || (layer?.type && layer.type === "nowcasting" && i === 0)) {
      if (!layer?.type || layer.type !== "nowcasting") {
        // For non-nowcasting layers, render a single solid line
        return (
          xScale.length && yScales[i] && ['line', 'area'].includes(layer.display) &&
            <AreaChart
              left={margin.left}
              data={layer.data}
              name={layer.name}
              width={width}
              yMax={yMax}
              xScale={xScale}
              yScale={yScales[i]}
              color={layer.color}
              key={`${layer.name}-${i}-linechart`}
              hideLeftAxis
              area={false}
              hideBottomAxis={true}
            />
          )
      }
      else{
        // For nowcasting layers, render solid line for past data and dotted line for future data  
        const testData = layer.data.slice(0, -layer.predict_values_count);
        const predictData = layer.data.slice(-(layer.predict_values_count+1));        
        return (
          <>
            {/* Render solid line for past data */}
            {xScale.length && yScales[i] && ['line', 'area'].includes(layer.display) && (
              <AreaChart
                left={margin.left}
                data={testData}
                name={`${layer.name}-past`}
                width={width}
                yMax={yMax}
                xScale={xScale}
                yScale={yScales[i]}
                color={layer.color}
                key={`${layer.name}-${i}-linechart-past`}
                hideLeftAxis
                area={false}
                hideBottomAxis={true}
                strokeDasharray={null} // Solid line for past data
              />
            )}
    
            {/* Render dotted line for future data */}
            {xScale.length && yScales[i] && ['line', 'area'].includes(layer.display) && predictData.length > 0 && (
              <AreaChart
                left={margin.left}
                data={predictData}
                name={`${layer.name}-future`}
                width={width}
                yMax={yMax}
                xScale={xScale}
                yScale={yScales[i]}
                color={layer.color}
                key={`${layer.name}-${i}-linechart-future`}
                hideLeftAxis
                area={false}
                hideBottomAxis={true}
                strokeDasharray="4 4" // Dotted line for future data
              />
            )}
          </>
          );
      }

    })

  ), [layers, xScale, yScales, margin, width, yMax]);
  
  return (
    <div>
      <svg width={parentWidth} height={parentHeight - 5}>
        { chartAxis }
        { chartsAreas }
        { chartsLines }
        <GraphTooltipArea margin={margin} width={width} height={graphHeight} xScale={xScale} yScales={yScales} layers={layers} tooltip={tooltip} />
      </svg>
      <GraphTooltipBubble height={graphHeight} width={width} tooltip={tooltip} xScale={xScale} margin={margin}/>
    </div>
  )
}

export default withParentSize(GraphChart)